import { ActionCreatorForDebouncedValue } from '../../../../configuration/setup/debouncedStateUpdate';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { SubmitEvent, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { RootState } from '../../../../configuration/setup/store';

interface ToolbarSearchProps {
    getSearchTerm: (state: RootState) => string;
    setSearchTerm: ActionCreatorForDebouncedValue<string>;
    tooltipMessageId: string;
    placeholderMessageId: string;
    maxLengthInput?: number | undefined;
}

export const ToolbarSearch = ({
    getSearchTerm,
    setSearchTerm,
    tooltipMessageId,
    placeholderMessageId,
    maxLengthInput,
}: ToolbarSearchProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const searchTerm = useAppSelector(getSearchTerm);

    const handleChange = useCallback(
        (newValue: string) => dispatch(setSearchTerm({ value: newValue })),
        [dispatch, setSearchTerm],
    );

    const clearSearch = useCallback(
        () => dispatch(setSearchTerm({ value: '', immediate: true })),
        [dispatch, setSearchTerm],
    );

    const handleSubmit = useCallback(
        (e: SubmitEvent<HTMLFormElement>) => {
            // We need this to avoid a page reload on first visit
            e.preventDefault();
            dispatch(setSearchTerm({ value: searchTerm, immediate: true }));
        },
        [dispatch, searchTerm, setSearchTerm],
    );

    return (
        <OverlayTrigger
            key="tableSearch"
            placement="top"
            overlay={
                <Tooltip id="tooltip" allowOnTouch>
                    <FormattedMessage id={tooltipMessageId} />
                </Tooltip>
            }
        >
            <form onSubmit={handleSubmit} aria-label="form">
                <TableSearch
                    placeholder={intl.formatMessage({ id: placeholderMessageId })}
                    onClear={clearSearch}
                    onChange={handleChange}
                    value={searchTerm}
                    maxLength={maxLengthInput}
                />
            </form>
        </OverlayTrigger>
    );
};
